import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../utils/dateLocale';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/hu';
import trainingBalanceService from '../services/trainingBalanceService';
import attendanceService, { MyPrivateLessonDebts } from '../services/attendanceService';
import { TrainingBalance as TrainingBalanceType, CancelledEvent, UpcomingEvent } from '../types/trainingBalance';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { venueService } from '../services/venueService';
import { UserListItem } from '../types/user';
import { userOptionLabel } from '../utils/userLabel';

/**
 * TrainingBalance component for players to view their own training balance
 * Admins can view any user's balance with filters
 */
const TrainingBalance: React.FC = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.userRole === 'Administrator';

  const [balance, setBalance] = useState<TrainingBalanceType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Private lesson debts (player's own)
  const [privateLessonDebts, setPrivateLessonDebts] = useState<MyPrivateLessonDebts | null>(null);

  // Cancelled events expand state
  const [cancelledExpanded, setCancelledExpanded] = useState(false);
  const [cancelledEvents, setCancelledEvents] = useState<CancelledEvent[]>([]);
  const [cancelledLoading, setCancelledLoading] = useState(false);
  const [cancelledLoaded, setCancelledLoaded] = useState(false);

  // Upcoming events expand state
  const [upcomingExpanded, setUpcomingExpanded] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingLoaded, setUpcomingLoaded] = useState(false);

  // Date range for balance calculation (default: last 4 months)
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(4, 'month'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());

  // Filters (Admin only)
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Load users and venues for filters (Admin only)
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadVenues();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const result = await userService.getUsers({});
      setUsers(result.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadVenues = async () => {
    try {
      const venuesData = await venueService.getActiveVenues();
      setVenues(venuesData);
    } catch (err) {
      console.error('Error loading venues:', err);
    }
  };

  // Fetch balance data
  const fetchBalance = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
        includeBreakdown: true,
      };

      let result: TrainingBalanceType;

      // Admin can select a specific user, otherwise show own balance
      if (isAdmin && selectedUserId) {
        result = await trainingBalanceService.getUserBalance(selectedUserId, params);
      } else {
        result = await trainingBalanceService.getMyBalance(params);
      }

      setBalance(result);
    } catch (err: any) {
      console.error('Error fetching training balance:', err);
      setError(apiErrorMessage(err, 'Nem sikerült betölteni az egyenleget'));
    } finally {
      setLoading(false);
    }
  };

  // Load balance on component mount and when dates/filters change
  useEffect(() => {
    fetchBalance();
    // Reset cancelled events when period changes
    setCancelledExpanded(false);
    setCancelledLoaded(false);
    setCancelledEvents([]);
  }, [startDate, endDate, selectedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load own private lesson debts (only for non-admin viewing own balance)
  useEffect(() => {
    if (!isAdmin || !selectedUserId) {
      attendanceService.getMyPrivateLessonDebts()
        .then(setPrivateLessonDebts)
        .catch(() => setPrivateLessonDebts(null));
    }
  }, [isAdmin, selectedUserId]);

  // Filter users by search term and venue
  const filteredUsers = users.filter((user) => {
    const matchesSearch = !searchTerm ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      // [C10, 2026-10-30] az e-mail `null` lehet (nem-admin hívónak a szerver más tagét nem adja ki,
      // `backend/PinyoApi/Services/UserListSanitizer.cs`) — a `.toLowerCase()` null-on összeomlana.
      (user.email ?? '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesVenue = !selectedVenueId || user.venueId === selectedVenueId;

    return matchesSearch && matchesVenue;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedVenueId('');
    setSelectedUserId('');
  };

  // Calculate attendance percentage
  const attendancePercentage = balance && balance.plannedTrainings > 0
    ? Math.round((balance.attendedTrainings / balance.plannedTrainings) * 100)
    : 0;

  const handleCancelledClick = async () => {
    const willExpand = !cancelledExpanded;
    setCancelledExpanded(willExpand);

    if (willExpand && !cancelledLoaded) {
      setCancelledLoading(true);
      try {
        const events = await trainingBalanceService.getMyCancelledEvents(
          startDate.toDate(),
          endDate.toDate()
        );
        setCancelledEvents(events);
        setCancelledLoaded(true);
      } catch (err) {
        console.error('Error loading cancelled events:', err);
      } finally {
        setCancelledLoading(false);
      }
    }
  };

  const handleUpcomingClick = async () => {
    const willExpand = !upcomingExpanded;
    setUpcomingExpanded(willExpand);

    if (willExpand && !upcomingLoaded) {
      setUpcomingLoading(true);
      try {
        const events = await trainingBalanceService.getMyUpcomingEvents();
        setUpcomingEvents(events);
        setUpcomingLoaded(true);
      } catch (err) {
        console.error('Error loading upcoming events:', err);
      } finally {
        setUpcomingLoading(false);
      }
    }
  };

  // Get selected user name for display
  const selectedUser = users.find(u => u.userId === selectedUserId);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu" localeText={HU_PICKER_LOCALE_TEXT}>
      <Box>
        <Typography variant="h4" gutterBottom>
          📊 {isAdmin && selectedUser ? `${selectedUser.name} - Edzés Statisztika` : 'Statisztika'}
        </Typography>

        {/* Filters (Admin only) */}
        {isAdmin && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Szűrők
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 auto' }, minWidth: 200 }}>
                  <TextField
                    fullWidth
                    label="Keresés (név vagy email)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                  />
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: '0 1 auto' }, minWidth: 150 }}>
                  <FormControl fullWidth>
                    <InputLabel>Helyszín</InputLabel>
                    <Select
                      value={selectedVenueId}
                      label="Helyszín"
                      onChange={(e) => setSelectedVenueId(e.target.value)}
                    >
                      <MenuItem value="">Összes</MenuItem>
                      {Array.isArray(venues) && venues.map((venue) => (
                        <MenuItem key={venue.venueId} value={venue.venueId}>
                          {venue.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 auto' }, minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel>Felhasználó</InputLabel>
                    <Select
                      value={selectedUserId}
                      label="Felhasználó"
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      <MenuItem value="">Saját egyenleg</MenuItem>
                      {filteredUsers.map((user) => (
                        <MenuItem key={user.userId} value={user.userId}>
                          {/* [C10, 2026-10-30] e-mail nélkül csak a név, nem „Név (null)" */}
                          {userOptionLabel(user)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={!searchTerm && !selectedVenueId && !selectedUserId}
                >
                  Törlés
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Date Range Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Időszak
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <DatePicker
                label="Kezdő dátum"
                value={startDate}
                onChange={(value) => {
                  if (value && dayjs.isDayjs(value) && value.isValid()) {
                    setStartDate(value);
                  }
                }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <DatePicker
                label="Záró dátum"
                value={endDate}
                onChange={(value) => {
                  if (value && dayjs.isDayjs(value) && value.isValid()) {
                    setEndDate(value);
                  }
                }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {!balance ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {isAdmin && !selectedUserId
              ? 'Válassz ki egy felhasználót az egyenleg megtekintéséhez, vagy hagyd üresen a saját egyenleged megtekintéséhez.'
              : 'Nincs elérhető adat a kiválasztott időszakra.'}
          </Alert>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Tervezett edzések
                    </Typography>
                    <Typography variant="h4">
                      {balance.plannedTrainings}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Részvett edzések
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {balance.attendedTrainings}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  onClick={balance.cancelledTrainings > 0 ? handleCancelledClick : undefined}
                  sx={{
                    cursor: balance.cancelledTrainings > 0 ? 'pointer' : 'default',
                    '&:hover': balance.cancelledTrainings > 0 ? { boxShadow: 4 } : {},
                    transition: 'box-shadow 0.2s',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Lemondott edzések
                      </Typography>
                      {balance.cancelledTrainings > 0 && (
                        cancelledExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />
                      )}
                    </Box>
                    <Typography variant="h4" color="warning.main">
                      {balance.cancelledTrainings}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Pótlási kredit: {balance.availableMakeupCredits}
                    </Typography>
                  </CardContent>
                  <Collapse in={cancelledExpanded}>
                    <Divider />
                    <Box sx={{ px: 2, pb: 2 }}>
                      {cancelledLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <List dense disablePadding>
                          {cancelledEvents.map((event) => (
                            <ListItem key={event.eventId} disableGutters sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={event.eventName}
                                secondary={`${event.eventDate} ${event.startTime.substring(0, 5)}-${event.endTime.substring(0, 5)} | ${event.venueName}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                          {cancelledEvents.length === 0 && !cancelledLoading && (
                            <Typography variant="caption" color="textSecondary" sx={{ py: 1, display: 'block' }}>
                              Nincs lemondott edzés ebben az időszakban
                            </Typography>
                          )}
                        </List>
                      )}
                    </Box>
                  </Collapse>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Hiányzott
                    </Typography>
                    <Typography variant="h4" color={balance.missedTrainings > 0 ? "error.main" : "text.primary"}>
                      {balance.missedTrainings}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Nem pótolható
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Hátralévő (tervezett)
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {balance.remainingPlannedTrainings}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Heti beosztás szerinti
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  onClick={(balance.upcomingRegistrations ?? 0) > 0 ? handleUpcomingClick : undefined}
                  sx={{
                    cursor: (balance.upcomingRegistrations ?? 0) > 0 ? 'pointer' : 'default',
                    '&:hover': (balance.upcomingRegistrations ?? 0) > 0 ? { boxShadow: 4 } : {},
                    transition: 'box-shadow 0.2s',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Bejelentkezve
                      </Typography>
                      {(balance.upcomingRegistrations ?? 0) > 0 && (
                        upcomingExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />
                      )}
                    </Box>
                    <Typography variant="h4" color="secondary.main">
                      {balance.upcomingRegistrations ?? 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Összes jövőbeli edzés
                    </Typography>
                  </CardContent>
                  <Collapse in={upcomingExpanded}>
                    <Divider />
                    <Box sx={{ px: 2, pb: 2 }}>
                      {upcomingLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <List dense disablePadding>
                          {upcomingEvents.map((event) => (
                            <ListItem key={event.eventId} disableGutters sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={event.eventName}
                                secondary={`${event.eventDate} ${event.startTime.substring(0, 5)}-${event.endTime.substring(0, 5)} | ${event.venueName || ''}${event.isFromPlannedSchedule ? '' : ' (egyéni)'}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                          {upcomingEvents.length === 0 && !upcomingLoading && (
                            <Typography variant="caption" color="textSecondary" sx={{ py: 1, display: 'block' }}>
                              Nincs jövőbeli bejelentkezés
                            </Typography>
                          )}
                        </List>
                      )}
                    </Box>
                  </Collapse>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Részvételi arány
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {attendancePercentage}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Private lesson debts - only shown when viewing own balance */}
            {(!isAdmin || !selectedUserId) && privateLessonDebts && privateLessonDebts.unpaidCount > 0 && (
              <Card sx={{ mb: 3, borderLeft: 4, borderColor: 'error.main' }}>
                <CardContent>
                  <Typography color="error" fontWeight="bold" gutterBottom>
                    {/* [Web UX 9 / T2, 2026-10-13] egységes „magánóra" írásmód (korábban „magán óra", `TrainingBalance.tsx:544`) */}
                    ⚠️ Kifizetetlen magánóra
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {privateLessonDebts.amountDue != null
                      ? `${new Intl.NumberFormat('hu-HU').format(privateLessonDebts.amountDue)} Ft`
                      : `${privateLessonDebts.unpaidCount} alkalom`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {privateLessonDebts.amountDue != null
                      ? `${privateLessonDebts.unpaidCount} alkalom`
                      : 'Összesen:'}{' '}
                    {privateLessonDebts.sessions.reduce((sum, s) => {
                      const [sh, sm] = s.startTime.split(':').map(Number);
                      const [eh, em] = s.endTime.split(':').map(Number);
                      return sum + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                    }, 0)} óra — kérjük rendezd az edződdel!
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Breakdown by Planned Event */}
            {balance.plannedEventBreakdown && balance.plannedEventBreakdown.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Részletes lebontás eseményenként
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {balance.plannedEventBreakdown.map((item, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {item.eventName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      {item.dayOfWeek} - {item.timeSlot}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                          Tervezett
                        </Typography>
                        <Typography variant="h6">{item.totalOccurrences}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                          Részvett
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {item.attended}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                          Lemondott
                        </Typography>
                        <Typography variant="h6" color="warning.main">
                          {item.cancelled}
                        </Typography>
                      </Grid>
                      {item.missed > 0 && (
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="body2" color="textSecondary">
                            Hiányzott
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {item.missed}
                          </Typography>
                        </Grid>
                      )}
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                          Hátralévő
                        </Typography>
                        <Typography variant="h6">{item.remaining}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Paper>
            )}

            {/* Info Box */}
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Fogalmak:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                <ul>
                  <li><strong>Tervezett edzések:</strong> Azok az edzések, amelyekre be vagy osztva a Heti Beosztás szerint a kiválasztott időszakban</li>
                  <li><strong>Részt vett edzések:</strong> Azok az edzések, amelyeken megjelentél és az edző jelenlévőnek jelölt</li>
                  <li><strong>Lemondott edzések:</strong> Azok az edzések, amelyekről az esemény kezdete előtt leiratkoztál — ezek pótlási kreditet generálnak</li>
                  <li><strong>Hiányzott:</strong> Azok a múltbeli edzések, amelyekről nem iratkoztál le, de nem is jelölt jelenlévőnek az edző — ezek NEM pótolhatóak</li>
                  <li><strong>Hátralévő (tervezett):</strong> Jövőbeli edzések a Heti Beosztás szerint — amelyekre automatikusan be vagy osztva</li>
                  <li><strong>Bejelentkezve:</strong> Összes jövőbeli edzés amelyre be vagy jelentkezve (tervezett + egyéni jelentkezés)</li>
                </ul>
              </Typography>
            </Alert>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default TrainingBalance;
